// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the stream index's rows as the app reads them, and the package
 * manifests the stream gateway reads, from one set of rules.
 *
 * `dataService.ts` turns every row of the live `index.json` into a
 * `Dataset`; `datasetTaxonomy.packageOf` then says which AYNI XR package
 * the stream is sold in, by the row's kind, title and tags. The stream
 * gateway (AYNI-2D `workers/package-gateway`, `content-allowset.ts`)
 * learns the same thing from `packages/<name>.json` in the bucket: a list
 * of dataset ids per package, matched against the live index. Security
 * fix plan S-02 (DECISION 2, 2026-10-21): those manifests are built by
 * `scripts/build-stream-packages.ts` from the rules here, so Browse's
 * locks and the gateway's entitlement agree by construction and no rule
 * is written twice.
 *
 * Everything in this file is pure: no DOM, no `import.meta.env`, no
 * logger, so the script runs it under `tsx` and the browser bundles it.
 */

import type { Dataset, DatasetFormat } from '../types'
import { KIND_PREFIX, isEducationOnly, isPlusHighlight, packageOf, type StreamPackage } from './datasetTaxonomy'
import { isRightsHeld } from './rightsHold'
import { XR_HIGHLIGHTS, XR_LIVE } from './xrPlans'

export type RealtimeDashKind = NonNullable<Dataset['realtimeKind']>

export interface RealtimeDashIndex {
  baseUrl?: string
  generatedAt?: string
  /** schema 1.4 — shared colour standards by group id (`displayGroups.ts`). */
  displayGroups?: unknown
  datasets?: RealtimeDashEntry[]
}

/** One row of the stream host's `index.json`. */
export interface RealtimeDashEntry {
  id: string
  /** AYNI: published coverage, when the stream host sends it (`datasetCoverage.ts`). */
  bounds?: unknown
  geo?: { bounds?: unknown }
  name?: string
  display_name?: string
  description?: string
  provider?: string
  /** Legacy field. In schema <1.2 this carried `forecast` to mark a row;
   *  in 1.2+ it is `stream` and the kind lives in `dataProductType`;
   *  catalog 1.5 adds `image`, a still picture (`image` + `dsa`). */
  type?: string
  /** catalog 1.5 — a still-image row's picture (PNG, JPEG or WebP), its DSA's order-1 image layer. */
  image?: string
  /** schema 1.2+ — `realtime` | `forecast`. Preferred forecast signal. */
  dataProductType?: string
  categories?: string[]
  units?: string
  /** Absent on schema 1.3 release-descriptor rows, which are skipped. */
  mpd?: string
  dsa?: string
  /** schema 1.3 — `latest.json` pointer to an immutable release; not resolved yet. */
  releaseDescriptorUrl?: string
  /** schema 1.3 — frames carry values (luma-encoded), not colours. */
  valueEncoded?: boolean
  /** schema 1.3 — the frames carry an alpha channel (VP9 alpha). */
  alpha?: boolean
  /** schema 1.2+ — `global` | `regional`. */
  coverage?: string
  /** schema 1.2+ — human-readable org name (e.g. "NOAA Science On a Sphere"). */
  organization?: string
  /** schema 1.2+ — URL-style org slug used in the R2 path. */
  organizationSlug?: string
  /** schema 1.2+ — region slug for `coverage: regional` rows (e.g. "conus"). */
  region?: string
  /** schema 1.2+ — human-readable region name. */
  regionName?: string
  thumbnail?: string
  colorbar?: string
  /** AYNI — the reviewed rights, copied from the `.dsa` (`services/rights.ts`). */
  rights?: unknown
  /** schema 1.4 — the id of the colour standard this row follows. */
  displayGroup?: string
  /**
   * AYNI — the body the row shows when it is not the Earth: catalog 1.5's
   * optional `celestialBody` (absent = the Earth), repeating its DSA's; a
   * `body:<name>` keyword or tag says the same (offEarthBody.ts). A row
   * without either is still marked at load if its `.dsa` names a body
   * (main.ts resolveCelestialBody).
   */
  celestialBody?: string
  keywords?: string[]
  tags?: string[]
  /**
   * AYNI — when the stream was first published (ISO 8601), for Browse's
   * Newest sort, the "new since your last visit" badge and the Date added
   * filter. Not the index's `generatedAt`, which every row shares.
   */
  firstPublishedAt?: string
  /**
   * AYNI — ids of retired rows this row stands in for (the AEMET Canary
   * Islands rows merged into the Spain rows, 2026-10-30), so a saved
   * Khipu, link or room still finds the dataset.
   */
  legacyIds?: unknown
}

/** The id prefix every stream row's `Dataset` carries; the index id follows it. */
export const R2_DASH_PREFIX = 'R2_DASH_'

export const REALTIME_DASH_KIND_LABEL: Readonly<Record<RealtimeDashKind, string>> = {
  'real-time': 'Real Time',
  forecast: 'Forecast',
  projection: 'Projection',
  reanalysis: 'Reanalysis',
  historical: 'Historical',
}

/**
 * A row's kind. Projections, reanalyses and historical records (AYNI,
 * 2026-10-02 / 2026-10-04) are titled by their declared type, as the
 * desktop apps show it, not "Real Time". The type is what says so. Their
 * paths don't: the newer ones all arrive under `/observed/`, whichever of
 * the two they are.
 */
export function realtimeDashKind(entry: RealtimeDashEntry): RealtimeDashKind {
  const path = entry.mpd ?? entry.releaseDescriptorUrl ?? entry.image ?? ''
  if (entry.dataProductType === 'projection' || /\/projection\//.test(path)) return 'projection'
  if (entry.dataProductType === 'forecast' || entry.type === 'forecast' || /\/forecast\//.test(path)) return 'forecast'
  if (entry.dataProductType === 'reanalysis' || /\/reanalysis\//.test(path)) return 'reanalysis'
  // A still picture (catalog 1.5) is never live.
  if (entry.dataProductType === 'historical' || isStillImageRow(entry)) return 'historical'
  return 'real-time'
}

/** The image format of a still picture, by its extension, or null when it is none of PNG, JPEG or WebP. */
export function stillImageFormat(path: string | undefined): DatasetFormat | null {
  const ext = /\.([a-z0-9]+)(?:[?#].*)?$/i.exec(path ?? '')?.[1]?.toLowerCase()
  if (ext === 'png') return 'image/png'
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
  if (ext === 'webp') return 'image/webp'
  return null
}

/**
 * Catalog 1.5: a still picture is a row of type "image" with an `image`, a
 * `dsa`, and neither an `mpd` nor a `releaseDescriptorUrl` (the
 * catalogue's oneOf). Anything else of type "image" is left out.
 */
export function isStillImageRow(entry: RealtimeDashEntry): boolean {
  return entry.type === 'image' && !!entry.image && !!entry.dsa && !entry.mpd && !entry.releaseDescriptorUrl
    && stillImageFormat(entry.image) !== null
}

/** A row that names its MPD directly. */
export function isDirectRow(entry: RealtimeDashEntry): entry is RealtimeDashEntry & { mpd: string } {
  return !!entry.id && !!entry.mpd
}

/** A release row (dashRelease.ts): a `latest.json` pointer, resolved at load. */
export function isReleaseRow(entry: RealtimeDashEntry): entry is RealtimeDashEntry & { releaseDescriptorUrl: string } {
  return !!entry.id && !entry.mpd && !!entry.releaseDescriptorUrl
}

/**
 * AYNI (owner, 2026-10-30: no SOS real-time datasets in the catalogue,
 * "keep global cloud cover the other two can be removed"): index rows the
 * app never ships though the index still lists them, as
 * `<organizationSlug>/<name>`. The VPS's re-streams of the SOS fire and
 * snow-and-ice feeds; NOAA SOS's Global Cloud Cover stays (it also feeds
 * the cloud layer, `cloudFrame.ts`). Replacements:
 * `docs/AYNI_XR_SOS_REALTIME_GAPS.md`.
 */
export const RETIRED_STREAMS: ReadonlySet<string> = new Set<string>([
  'noaa-sos/fires', // Active Fires (NOAA SOS) → Active Fires (FIRMS)
  'noaa-sos/snow_ice', // Snow & Ice Extent → Snow Cover Extent (CLMS)
])

/** Whether an index row is retired (`RETIRED_STREAMS`). */
export function isRetiredStream(entry: Pick<RealtimeDashEntry, 'organizationSlug' | 'name'>): boolean {
  return !!entry.organizationSlug && !!entry.name && RETIRED_STREAMS.has(`${entry.organizationSlug}/${entry.name}`)
}

/**
 * The rows of an index that become datasets, in the app's order: direct
 * MPD rows, then releases, then still pictures. A row with no id, with
 * none of the three, held for rights (`rightsHold.ts`; owner,
 * 2026-10-06: a stream we have no right to stream is not shipped at all)
 * or retired (`RETIRED_STREAMS`) is left out, as
 * `dataService.fetchRealtimeDashDatasets` leaves it out.
 */
export function streamRowsOf(entries: readonly RealtimeDashEntry[] | undefined): RealtimeDashEntry[] {
  const shipped = (entries ?? []).filter((entry) => !isRightsHeld(entry.name) && !isRetiredStream(entry))
  return [
    ...shipped.filter(isDirectRow),
    ...shipped.filter(isReleaseRow),
    ...shipped.filter((entry) => !!entry.id && isStillImageRow(entry)),
  ]
}

export function prefixedRealtimeDashTitle(rawTitle: string, kind: RealtimeDashKind): string {
  const cleanTitle = rawTitle.trim() || 'Untitled dataset'
  const titleWithoutKind = cleanTitle.replace(KIND_PREFIX, '')
  // A projection named the old way already says what it is ("Projected
  // Surface Temperature Change (CMIP6): SSP2-4.5"); a prefix would repeat
  // it. The publisher's 2026-10 names drop "Projected" ("Sea Level Rise
  // (IPCC AR6): SSP2-4.5"), and those get the prefix like every other kind.
  if (kind === 'projection' && /^Projected\b/i.test(titleWithoutKind)) return titleWithoutKind
  return `${REALTIME_DASH_KIND_LABEL[kind]}: ${titleWithoutKind}`
}

/** A stream row's tags: its kind's label, DASH (not for a still picture), its categories and its unit. */
export function streamTags(entry: RealtimeDashEntry, kind: RealtimeDashKind): string[] {
  return Array.from(new Set([
    REALTIME_DASH_KIND_LABEL[kind],
    ...(isStillImageRow(entry) ? [] : ['DASH']),
    ...(entry.categories ?? []),
    ...(entry.units ? [entry.units] : []),
  ]))
}

/** The fields of a stream's `Dataset` that the taxonomy rules read. */
export type StreamShape = Pick<Dataset, 'id' | 'title' | 'tags' | 'realtimeKind'>

/**
 * A row as the taxonomy sees it: the same id, title, tags and kind
 * `dataService.realtimeDashDataset` gives the full `Dataset`.
 */
export function streamShapeOf(entry: RealtimeDashEntry): StreamShape {
  const kind = realtimeDashKind(entry)
  return {
    id: `${R2_DASH_PREFIX}${entry.id}`,
    title: prefixedRealtimeDashTitle(entry.display_name ?? entry.name ?? entry.id, kind),
    tags: streamTags(entry, kind),
    realtimeKind: kind,
  }
}

/** The manifests the gateway reads, in the order they are written. */
export const STREAM_MANIFESTS = [
  'xr-atmosphere',
  'xr-ocean',
  'xr-climate',
  'xr-fire-aq',
  'xr-hazards',
  XR_LIVE,
  XR_HIGHLIGHTS,
  'xr-education',
] as const
export type StreamManifestName = (typeof STREAM_MANIFESTS)[number]

/** Each manifest's title for people (English; the bucket has no locale). */
export const STREAM_MANIFEST_TITLE: Readonly<Record<StreamManifestName, string>> = {
  'xr-atmosphere': 'Atmosphere & Weather',
  'xr-ocean': 'Ocean',
  'xr-climate': 'Climate',
  'xr-fire-aq': 'Fire, Smoke & Air Quality',
  'xr-hazards': 'Hazards and events',
  'xr-live': 'Live Earth: every real-time stream and forecast',
  'xr-highlights': 'Climate highlights',
  'xr-education': 'Streams for Education plans only',
}

/** One row of a manifest: the index id the gateway matches, and the name for whoever reads the file. */
export interface StreamManifestRow {
  id: string
  name?: string
}

export interface StreamManifest {
  package: StreamManifestName
  title: string
  datasets: StreamManifestRow[]
}

/** A stream row and the package the taxonomy gives it (null: no package). */
export interface AssignedRow {
  id: string
  name?: string
  title: string
  kind: RealtimeDashKind
  package: StreamPackage | null
  live: boolean
  highlight: boolean
  educationOnly: boolean
}

export interface StreamPackageAssignment {
  manifests: StreamManifest[]
  rows: AssignedRow[]
  /** Rows no package manifest names (the five sold packages); `xr-live` may still carry them. */
  unpackaged: AssignedRow[]
}

/**
 * Every shipped row of an index assigned to its manifests:
 *  - one of the five packages, by `packageOf` (none → `unpackaged`);
 *  - `xr-live`: every real-time stream and forecast (Plus's Live Earth);
 *  - `xr-highlights`: `PLUS_HIGHLIGHTS` by id, those present in the index;
 *  - `xr-education`: `EDUCATION_ONLY_STREAMS` by id, those present
 *    (Education plans get `*`; the manifest records the list).
 * Rows are sorted by id so the same index gives the same bytes.
 *
 * Throws when the index has no shipped rows or a row lacks a string id:
 * a manifest with no ids would silently empty a package at the gateway.
 */
export function assignStreamPackages(index: RealtimeDashIndex): StreamPackageAssignment {
  for (const entry of index.datasets ?? []) {
    if (typeof entry.id !== 'string' || entry.id.trim() === '') {
      throw new Error(`index row without an id: ${JSON.stringify(entry).slice(0, 200)}`)
    }
  }
  const rows: AssignedRow[] = streamRowsOf(index.datasets).map((entry) => {
    const shape = streamShapeOf(entry)
    const kind = shape.realtimeKind as RealtimeDashKind
    return {
      id: entry.id,
      ...(entry.name ? { name: entry.name } : {}),
      title: shape.title,
      kind,
      package: packageOf(shape),
      live: kind === 'real-time' || kind === 'forecast',
      highlight: isPlusHighlight(shape),
      educationOnly: isEducationOnly(shape),
    }
  })
  if (rows.length === 0) throw new Error('the index has no stream rows')
  rows.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))

  const members = (name: StreamManifestName): AssignedRow[] => {
    if (name === XR_LIVE) return rows.filter((r) => r.live)
    if (name === XR_HIGHLIGHTS) return rows.filter((r) => r.highlight)
    if (name === 'xr-education') return rows.filter((r) => r.educationOnly)
    return rows.filter((r) => r.package === name)
  }
  const manifests = STREAM_MANIFESTS.map((name): StreamManifest => ({
    package: name,
    title: STREAM_MANIFEST_TITLE[name],
    datasets: members(name).map((r) => ({ id: r.id, ...(r.name ? { name: r.name } : {}) })),
  }))
  return { manifests, rows, unpackaged: rows.filter((r) => r.package === null) }
}
